/**
 * Currency/Localization Popup System
 * Independent location detection and currency switching
 */

/**
 * @method getBrowsingContextSuggestions
 * @description Gets browsing context suggestions based on visitor's browser settings and IP address
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableCountry - Whether to get country suggestions (default: true)
 * @param {boolean} options.enableLanguage - Whether to get language suggestions (default: true)
 * @param {string[]} options.excludeCountries - Additional countries to exclude
 * @param {string[]} options.excludeLanguages - Additional languages to exclude
 * @returns {Promise<Object|null>} The browsing context suggestions or null if failed
 */
async function getBrowsingContextSuggestions({
  enableCountry = true,
  enableLanguage = true,
  excludeCountries = [],
  excludeLanguages = [],
} = {}) {
  try {
    const params = new URLSearchParams();

    // Country suggestions
    if (enableCountry) {
      params.append("country[enabled]", "true");
      const countriesToExclude = [
        window.Shopify.country,
        ...excludeCountries,
      ].filter(Boolean);
      countriesToExclude.forEach((country) =>
        params.append("country[exclude]", country)
      );
    }

    // Language suggestions
    if (enableLanguage) {
      params.append("language[enabled]", "true");
      const languagesToExclude = [
        window.Shopify.language,
        ...excludeLanguages,
      ].filter(Boolean);
      languagesToExclude.forEach((language) =>
        params.append("language[exclude]", language)
      );
    }

    const response = await fetch(
      `${window.Shopify.routes.root}browsing_context_suggestions.json?${params}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const suggestions = await response.json();
    console.log("Browsing context suggestions:", suggestions);
    return suggestions;
  } catch (error) {
    console.error("Error getting browsing context suggestions:", error);
    return null;
  }
}

/**
 * @method setLocalization
 * @description Sets the localization for the current browsing context
 * @param {Object} options - The options object
 * @param {string} options.country - The country code
 * @param {string} options.language - The language code
 * @param {string} options.redirectTo - Optional URL to redirect to after localization change
 * @returns {Promise<boolean>} Promise that resolves to success status
 */
async function setLocalization({
  country = window.Shopify.country,
  language = window.Shopify.language,
  redirectTo = null,
} = {}) {
  // Validate inputs
  if (!country || !language) {
    console.error("Invalid country or language code provided");
    return false;
  }

  // Check if localization is already set to avoid unnecessary requests
  if (
    country === window.Shopify.country &&
    language === window.Shopify.language
  ) {
    console.warn("Localization already set to requested values");
    return true;
  }

  try {
    const formId = `localization-form-${Date.now()}`;
    const form = document.createElement("form");
    form.id = formId;
    form.action = "/localization";
    form.method = "POST";
    form.style.display = "none";

    // Create form inputs
    const methodInput = document.createElement("input");
    methodInput.name = "_method";
    methodInput.value = "PUT";
    form.appendChild(methodInput);

    const countryInput = document.createElement("input");
    countryInput.name = "country_code";
    countryInput.value = country;
    form.appendChild(countryInput);

    const languageInput = document.createElement("input");
    languageInput.name = "language_code";
    languageInput.value = language;
    form.appendChild(languageInput);

    // Add redirect URL if provided
    if (redirectTo) {
      const returnToInput = document.createElement("input");
      returnToInput.name = "return_to";
      returnToInput.value = redirectTo;
      form.appendChild(returnToInput);
    }

    document.body.appendChild(form);
    form.submit();

    return true;
  } catch (error) {
    console.error("Error setting localization:", error);
    return false;
  }
}

/**
 * Currency Popup Manager
 * Enhanced popup system with independent location detection
 */
window.CurrencyPopup = (function () {
  let popupInstance = null;
  let isInitialized = false;
  let detectedLocation = null;

  // Currency mapping for display
  const CURRENCY_MAP = {
    US: { symbol: "$", code: "USD", name: "Dollar" },
    CA: { symbol: "C$", code: "CAD", name: "Canadian Dollar" },
    GB: { symbol: "£", code: "GBP", name: "Pound" },
    AU: { symbol: "A$", code: "AUD", name: "Australian Dollar" },
    EU: { symbol: "€", code: "EUR", name: "Euro" },
    // EU Countries
    DE: { symbol: "€", code: "EUR", name: "Euro" },
    FR: { symbol: "€", code: "EUR", name: "Euro" },
    ES: { symbol: "€", code: "EUR", name: "Euro" },
    IT: { symbol: "€", code: "EUR", name: "Euro" },
    BE: { symbol: "€", code: "EUR", name: "Euro" },
    NL: { symbol: "€", code: "EUR", name: "Euro" },
    AT: { symbol: "€", code: "EUR", name: "Euro" },
    PT: { symbol: "€", code: "EUR", name: "Euro" },
    IE: { symbol: "€", code: "EUR", name: "Euro" },
    FI: { symbol: "€", code: "EUR", name: "Euro" },
    GR: { symbol: "€", code: "EUR", name: "Euro" },
    LU: { symbol: "€", code: "EUR", name: "Euro" },
    // Other currencies
    JP: { symbol: "¥", code: "JPY", name: "Yen" },
    CN: { symbol: "¥", code: "CNY", name: "Yuan" },
    NZ: { symbol: "NZ$", code: "NZD", name: "New Zealand Dollar" },
  };

  /**
   * Get currency info for a country
   * @param {string} countryCode - Country code (e.g., 'US')
   * @returns {Object} Currency information
   */
  function getCurrencyInfo(countryCode) {
    return (
      CURRENCY_MAP[countryCode] || { symbol: "$", code: "USD", name: "Dollar" }
    );
  }

  /**
   * Get country name from country code
   * @param {string} countryCode - Country code (e.g., 'US')
   * @returns {string} Country name
   */
  function getCountryName(countryCode) {
    const countryNames = {
      US: "United States",
      CA: "Canada",
      GB: "United Kingdom",
      AU: "Australia",
      NZ: "New Zealand",
      // EU Countries
      FR: "France",
      DE: "Germany",
      ES: "Spain",
      IT: "Italy",
      BE: "Belgium",
      NL: "Netherlands",
      AT: "Austria",
      PT: "Portugal",
      IE: "Ireland",
      FI: "Finland",
      GR: "Greece",
      LU: "Luxembourg",
      // Other
      BM: "Bermuda",
      JP: "Japan",
      CN: "China",
    };
    return countryNames[countryCode] || countryCode;
  }

  /**
   * Detect user location using multiple methods
   * @returns {Promise<Object>} User location data
   */
  async function detectUserLocation() {
    console.log("🌍 Starting location detection...");

    // Method 1: Try Shopify's browsing context API first
    try {
      console.log("🔍 Trying Shopify browsing context API...");
      const response = await fetch(
        "/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true"
      );
      if (response.ok) {
        const data = await response.json();
        if (data.detected_values && data.detected_values.country) {
          console.log("✅ Shopify API success:", data.detected_values);
          return {
            country: data.detected_values.country.handle,
            countryName: data.detected_values.country.name,
            language: data.detected_values.language?.handle || "en",
            method: "shopify-api",
          };
        }
      }
    } catch (error) {
      console.log("❌ Shopify API failed:", error.message);
    }

    // Method 2: Browser timezone detection
    try {
      console.log("🕐 Trying timezone detection...");
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log("🕐 Detected timezone:", timezone);

      const timezoneToCountry = {
        "America/New_York": "US",
        "America/Chicago": "US",
        "America/Denver": "US",
        "America/Los_Angeles": "US",
        "America/Phoenix": "US",
        "America/Anchorage": "US",
        "America/Toronto": "CA",
        "America/Vancouver": "CA",
        "America/Montreal": "CA",
        "America/Halifax": "CA",
        "Europe/London": "GB",
        "Europe/Paris": "FR",
        "Europe/Berlin": "DE",
        "Europe/Rome": "IT",
        "Europe/Madrid": "ES",
        "Europe/Brussels": "BE",
        "Europe/Amsterdam": "NL",
        "Europe/Vienna": "AT",
        "Europe/Lisbon": "PT",
        "Europe/Dublin": "IE",
        "Europe/Helsinki": "FI",
        "Europe/Athens": "GR",
        "Europe/Luxembourg": "LU",
        "Australia/Sydney": "AU",
        "Australia/Melbourne": "AU",
        "Australia/Brisbane": "AU",
        "Australia/Perth": "AU",
        "Pacific/Auckland": "NZ",
        "Atlantic/Bermuda": "BM",
      };

      if (timezoneToCountry[timezone]) {
        const country = timezoneToCountry[timezone];
        console.log("✅ Timezone detection success:", country);
        return {
          country: country,
          countryName: getCountryName(country),
          language: "en",
          method: "timezone",
        };
      }
    } catch (error) {
      console.log("❌ Timezone detection failed:", error.message);
    }

    // Method 3: Browser language detection
    try {
      console.log("🗣️ Trying browser language detection...");
      const language = navigator.language || navigator.userLanguage;
      console.log("🗣️ Browser language:", language);

      const countryFromLang = language.split("-")[1]?.toUpperCase();

      if (
        countryFromLang &&
        ["US", "CA", "GB", "AU", "NZ", "FR", "DE", "ES", "IT", "BE", "NL", "AT", "PT", "IE", "FI", "GR", "LU"].includes(
          countryFromLang
        )
      ) {
        console.log("✅ Language detection success:", countryFromLang);
        return {
          country: countryFromLang,
          countryName: getCountryName(countryFromLang),
          language: language.split("-")[0],
          method: "browser-language",
        };
      }
    } catch (error) {
      console.log("❌ Language detection failed:", error.message);
    }

    // Method 4: IP-based detection (using a free service)
    try {
      console.log("🌐 Trying IP-based detection...");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch("https://ipapi.co/json/", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      if (data.country_code) {
        console.log("✅ IP detection success:", data);
        return {
          country: data.country_code.toUpperCase(),
          countryName: data.country_name,
          language: "en",
          method: "ip-api",
        };
      }
    } catch (error) {
      console.log("❌ IP detection failed:", error.message);
    }

    // Fallback: Assume US
    console.log("🔄 Using fallback: US");
    return {
      country: "US",
      countryName: "United States",
      language: "en",
      method: "fallback",
    };
  }

  /**
   * Get current market from Shopify data
   * @returns {Object} Current market info
   */
  function getCurrentMarket() {
    const currentCountry = window.Shopify?.country || "GB";
    const currentLanguage = window.Shopify?.locale || "en";

    return {
      country: currentCountry,
      language: currentLanguage,
      countryName: getCountryName(currentCountry),
    };
  }

  /**
   * Check if user should see currency popup
   * @param {Object} detectedLocation - User's detected location
   * @param {Object} currentMarket - Current market
   * @returns {boolean} Whether to show popup
   */
  function shouldShowPopup(detectedLocation, currentMarket) {
    console.log("🤔 Checking if popup should show...");
    console.log("🤔 Detected:", detectedLocation);
    console.log("🤔 Current:", currentMarket);

    // Don't show if same country
    if (detectedLocation.country === currentMarket.country) {
      console.log("❌ Same country, no popup needed");
      return false;
    }

    // Don't show if detected location method is unreliable
    if (detectedLocation.method === "fallback") {
      console.log("❌ Fallback method, not reliable enough");
      return false;
    }

    // Don't show for certain country combinations that are similar
    const similarPairs = [
      ["US", "CA"], // US/Canada often have similar preferences
      ["GB", "AU"], // UK/Australia
      ["GB", "NZ"], // UK/New Zealand
    ];

    for (const pair of similarPairs) {
      if (
        pair.includes(detectedLocation.country) &&
        pair.includes(currentMarket.country)
      ) {
        // Only show if there's a significant currency difference
        const detectedCurrency = getCurrencyInfo(detectedLocation.country);
        const currentCurrency = getCurrencyInfo(currentMarket.country);
        const showForCurrency = detectedCurrency.code !== currentCurrency.code;
        console.log(
          `🤔 Similar countries (${pair.join(
            "/"
          )}), show for currency difference: ${showForCurrency}`
        );
        return showForCurrency;
      }
    }

    console.log("✅ Should show popup");
    return true;
  }

  /**
   * Create enhanced popup HTML
   * @param {Object} detectedLocation - User's detected location
   * @param {Object} currentMarket - Current market
   * @returns {HTMLElement} Popup element
   */
  function createPopup(detectedLocation, currentMarket) {
    const currentCurrency = getCurrencyInfo(currentMarket.country);
    const suggestedCurrency = getCurrencyInfo(detectedLocation.country);

    const popup = document.createElement("div");
    popup.className = "currency-popup-overlay";
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "true");
    popup.setAttribute("aria-labelledby", "currency-popup-title");

    popup.innerHTML = `
      <div class="currency-popup">
        <div class="currency-popup__header">
          <h3 id="currency-popup-title" class="currency-popup__title">
            Switch to your local site?
          </h3>
          <button class="currency-popup__close" aria-label="Close popup">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        
        <div class="currency-popup__content">
          <div class="currency-popup__message">
            <p>We've detected you're browsing from <strong>${detectedLocation.countryName}</strong>.</p>
            <p>Would you like to switch to your local site for more accurate pricing?</p>
          </div>
          
          <div class="currency-popup__comparison">
            <div class="currency-popup__option currency-popup__option--current">
              <div class="currency-popup__flag">🌐</div>
              <div class="currency-popup__details">
                <div class="currency-popup__country">${currentMarket.countryName}</div>
                <div class="currency-popup__currency">${currentCurrency.symbol} ${currentCurrency.code}</div>
              </div>
            </div>
            
            <div class="currency-popup__arrow">→</div>
            
            <div class="currency-popup__option currency-popup__option--suggested">
              <div class="currency-popup__flag">🌍</div>
              <div class="currency-popup__details">
                <div class="currency-popup__country">${detectedLocation.countryName}</div>
                <div class="currency-popup__currency">${suggestedCurrency.symbol} ${suggestedCurrency.code}</div>
              </div>
            </div>
          </div>
        
        </div>
        
        <div class="currency-popup__actions">
          <button class="currency-popup__button currency-popup__button--primary" data-action="switch">
            Switch to ${suggestedCurrency.code}
          </button>
          <button class="currency-popup__button currency-popup__button--secondary" data-action="stay">
            Continue with ${currentCurrency.code}
          </button>
        </div>
        
        <div class="currency-popup__footer">
          <label class="currency-popup__checkbox">
            <input type="checkbox" id="currency-popup-remember">
            Don't ask again
          </label>
        </div>
      </div>
    `;

    return popup;
  }

  /**
   * Create and inject popup styles
   */
  function createStyles() {
    if (document.getElementById("currency-popup-styles")) return;

    const style = document.createElement("style");
    style.id = "currency-popup-styles";
    style.textContent = `
      .currency-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        animation: fadeIn 0.3s ease-out;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes slideUp {
        from { transform: translateY(30px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      .currency-popup {
        background: white;
        max-width: 480px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease-out;
        border: none;
      }
      
      .currency-popup__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px 24px 16px 24px;
        border-bottom: none;
      }
      
      .currency-popup__title {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        color: black;
      }
      
      .currency-popup__close {
        background: none;
        border: none;
        padding: 8px;
        cursor: pointer;
        color: black;
        transition: all 0.2s ease;
      }
      
      .currency-popup__close:hover {
        background: black;
        color: white;
      }
      
      .currency-popup__content {
        padding: 24px;
      }
      
      .currency-popup__message {
        margin-bottom: 24px;
        text-align: center;
      }
      
      .currency-popup__message p {
        margin: 0 0 8px 0;
        color: black;
        line-height: 1.5;
        font-weight: 500;
      }
      
      .currency-popup__comparison {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 24px 0;
        padding: 20px;
        background: #f0f0f0;
        border: none;
      }
      
      .currency-popup__option {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
      }
      
      .currency-popup__flag {
        font-size: 24px;
        width: 40px;
        height: 40px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .currency-popup__details {
        flex: 1;
      }
      
      .currency-popup__country {
        font-weight: 700;
        color: black;
        font-size: 14px;
      }
      
      .currency-popup__currency {
        color: black;
        font-size: 13px;
        margin-top: 2px;
        font-weight: 600;
      }
      
      .currency-popup__arrow {
        margin: 0 16px;
        font-size: 18px;
        color: black;
        font-weight: bold;
      }
      
      .currency-popup__benefits {
        margin: 20px 0;
      }
      
      .currency-popup__benefits ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      
      .currency-popup__benefits li {
        background: black;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 600;
        border: 1px solid black;
      }
      
      .currency-popup__actions {
        padding: 0 24px 20px 24px;
        display: flex;
        gap: 12px;
      }
      
      .currency-popup__button {
        flex: 1;
        padding: 14px 20px;
        border: 1px solid rgb(0, 0, 0, 0.8);
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 14px;
      }
      
      .currency-popup__button--primary {
        background: black;
        color: white;
      }
      
      .currency-popup__button--primary:hover {
          background: #272727;
          color: #ffffff;
      }
      
      .currency-popup__button--secondary {
        background: white;
        color: black;
      }
      
      .currency-popup__button--secondary:hover {
        background: black;
        color: white;
      }
      
      .currency-popup__footer {
        padding: 0 24px 24px 24px;
        border-top: none;
        padding-top: 16px;
      }
      
      .currency-popup__checkbox {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: black;
        cursor: pointer;
        user-select: none;
        font-weight: 600;
      }
      
      .currency-popup__checkbox input {
        margin: 0;
        width: 16px;
        height: 16px;
        border: none;
        background: white;
      }
      
      .currency-popup__checkbox input:checked {
        background: black;
      }
      
      @media (max-width: 600px) {
        .currency-popup {
          margin: 10px;
        }
        
        .currency-popup__comparison {
          flex-direction: column;
          gap: 16px;
        }
        
        .currency-popup__arrow {
          transform: rotate(90deg);
          margin: 0;
        }
        
        .currency-popup__actions {
          flex-direction: column;
        }
        
        .currency-popup__benefits ul {
          justify-content: center;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Show the currency popup
   * @param {Object} options - Configuration options
   * @param {Function} options.onSwitch - Callback when user chooses to switch
   * @param {Function} options.onStay - Callback when user chooses to stay
   * @param {Function} options.onClose - Callback when popup is closed
   */
  async function show(options = {}) {
    console.log("🚀 Currency popup show() called");

    if (popupInstance) {
      hide();
    }

    // Get location if not already detected
    if (!detectedLocation) {
      console.log("🔍 No cached location, detecting...");
      detectedLocation = await detectUserLocation();
    }

    const currentMarket = getCurrentMarket();

    if (!shouldShowPopup(detectedLocation, currentMarket)) {
      console.log("❌ Popup not needed");
      return;
    }

    console.log("✅ Showing currency popup");
    createStyles();
    popupInstance = createPopup(detectedLocation, currentMarket);
    document.body.appendChild(popupInstance);

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Add event listeners
    const switchBtn = popupInstance.querySelector('[data-action="switch"]');
    const stayBtn = popupInstance.querySelector('[data-action="stay"]');
    const closeBtn = popupInstance.querySelector(".currency-popup__close");
    const rememberCheckbox = popupInstance.querySelector(
      "#currency-popup-remember"
    );

    function cleanup() {
      document.body.style.overflow = "";
      if (popupInstance) {
        popupInstance.remove();
        popupInstance = null;
      }
    }

    function handleSwitch() {
      const remember = rememberCheckbox.checked;
      cleanup();

      if (remember) {
        localStorage.setItem(
          "currency-popup-choice",
          JSON.stringify({
            choice: "switch",
            market: detectedLocation.country,
            timestamp: Date.now(),
          })
        );
      }

      if (options.onSwitch) {
        options.onSwitch({
          country: detectedLocation.country,
          language: detectedLocation.language,
        });
      } else {
        // Use setLocalization to switch markets
        setLocalization({
          country: detectedLocation.country,
          language: detectedLocation.language,
        });
      }
    }

    function handleStay() {
      const remember = rememberCheckbox.checked;
      cleanup();

      if (remember) {
        localStorage.setItem(
          "currency-popup-choice",
          JSON.stringify({
            choice: "stay",
            market: currentMarket.country,
            suggested: detectedLocation.country,
            timestamp: Date.now(),
          })
        );
      }

      if (options.onStay) {
        options.onStay();
      }
    }

    function handleClose() {
      cleanup();
      if (options.onClose) {
        options.onClose();
      }
    }

    switchBtn.addEventListener("click", handleSwitch);
    stayBtn.addEventListener("click", handleStay);
    closeBtn.addEventListener("click", handleClose);

    // Close on overlay click
    popupInstance.addEventListener("click", function (e) {
      if (e.target === popupInstance) {
        handleClose();
      }
    });

    // Close on escape key
    function handleEscape(e) {
      if (e.key === "Escape") {
        handleClose();
        document.removeEventListener("keydown", handleEscape);
      }
    }
    document.addEventListener("keydown", handleEscape);
  }

  /**
   * Hide the popup
   */
  function hide() {
    if (popupInstance) {
      document.body.style.overflow = "";
      popupInstance.remove();
      popupInstance = null;
    }
  }

  /**
   * Check if user has previously made a choice
   * @returns {Object|null} User's previous choice
   */
  function getUserChoice() {
    try {
      const saved = localStorage.getItem("currency-popup-choice");
      if (saved) {
        const choice = JSON.parse(saved);
        // Expire choices after 30 days
        if (Date.now() - choice.timestamp > 30 * 24 * 60 * 60 * 1000) {
          localStorage.removeItem("currency-popup-choice");
          return null;
        }
        return choice;
      }
    } catch (e) {
      console.error("Error reading currency popup choice:", e);
    }
    return null;
  }

  /**
   * Initialize currency popup system
   * @param {Object} options - Configuration options
   * @param {boolean} options.autoShow - Show popup automatically (default: true)
   * @param {number} options.delay - Delay before showing popup in ms (default: 2000)
   * @param {boolean} options.respectChoices - Respect user's previous choices (default: true)
   */
  async function init(options = {}) {
    if (isInitialized) return;
    isInitialized = true;

    console.log("🎯 Currency popup initializing...");

    const { autoShow = true, delay = 2000, respectChoices = true } = options;

    if (!autoShow) {
      console.log("❌ Auto-show disabled");
      return;
    }

    // Check if user has made a previous choice
    if (respectChoices) {
      const userChoice = getUserChoice();
      if (userChoice) {
        console.log(
          "🧠 Currency popup: Respecting user choice:",
          userChoice.choice
        );
        return;
      }
    }

    // Detect location first
    console.log("🔍 Pre-detecting location...");
    detectedLocation = await detectUserLocation();

    // Show popup after delay
    setTimeout(() => {
      show(options);
    }, delay);
  }

  // Public API
  return {
    show: show,
    hide: hide,
    init: init,
    getUserChoice: getUserChoice,
    getBrowsingContextSuggestions: getBrowsingContextSuggestions,
    setLocalization: setLocalization,
    detectUserLocation: detectUserLocation,
    getCurrentMarket: getCurrentMarket,
  };
})();

/*
USAGE EXAMPLES:

// 1. SIMPLE AUTO-INITIALIZATION
// Just add this line to automatically show currency popup when needed
CurrencyPopup.init();

// 2. CUSTOM CONFIGURATION
CurrencyPopup.init({
  autoShow: true,        // Show automatically
  delay: 3000,          // Wait 3 seconds before showing
  respectChoices: true,  // Don't show again if user made a choice
  onSwitch: function(market) {
    console.log('User switching to:', market);
    // Custom switch logic here
  },
  onStay: function() {
    console.log('User chose to stay');
    // Custom stay logic here
  }
});

// 3. MANUAL CONTROL
// Show popup manually
CurrencyPopup.show({
  onSwitch: function(market) {
    console.log('Switching to market:', market);
    CurrencyPopup.setLocalization({
      country: market.country,
      language: market.language
    });
  },
  onStay: function() {
    console.log('User staying on current market');
  }
});

// 4. DEBUG FUNCTIONS
// Test location detection
CurrencyPopup.detectUserLocation().then(location => {
  console.log('Your detected location:', location);
});

// Check current market
console.log('Current market:', CurrencyPopup.getCurrentMarket());

// Set localization programmatically
CurrencyPopup.setLocalization({
  country: 'CA',
  language: 'en',
  redirectTo: '/products/special-offer'
});
*/
